// The account assistant's changes to the product creator (PRO-3491, phase 2). The assistant
// proposes a change with what it expects the field to hold now (`expect`, from the snapshot it
// last saw) and what it becomes (`value`); the creator applies it only if the field still holds
// `expect` (compare-and-set), through the same handler its own button calls, so the creator's
// checks and side effects run exactly as for a click. A field the seller changed in the
// meantime comes back "stale" with its current value; other fields don't conflict. Nothing
// here submits anything: the seller still presses Submit.

import { DateTime } from "luxon";
import { colorName, itemName, money, placementBox, priceBoxes, printMethods } from "@/lib/creator-assistant-snapshot.js";
import { calculateArtworkGeometry } from "@/lib/geometry.js";
import { getCustomSize, placementToBoxMap } from "@/lib/product-creator.js";

// A custom placement needs the size and offset controls, so it stays a click.
const isCustomPlacement = (name) => /(^|\+)custom(\+|$)/.test(name);

const applied = (now) => ({ status: "applied", now });
const stale = (now) => ({ status: "stale", now });
const refused = (message) => ({ status: "refused", message });

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const isHex = (value) => /^#[0-9a-f]{6}$/i.test(value);

// Names compared loosely: case, and a sticker size's spellings ("3x3", "3″ ✕ 3″", "3″").
const norm = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[″"\s]/g, "")
    .replace(/[✕×]/g, "x");
const sizeNorm = (value) => {
  const name = norm(value);

  return /^\d+(\.\d+)?$/.test(name) ? `${name}x${name}` : name;
};

function colorNames(vm) {
  return vm.input.colors.map(colorName);
}

// The named swatches this product type picks from. Phone cases take any hex colour as well (the
// picker's custom swatch); stickers pick a size from their own list. Drinkware has one colour,
// picked on the page (the colour panel offers no add or remove once it's set), so none here.
function bundleList(vm) {
  const types = vm.productTypes;

  return (
    {
      [types.APPAREL]: vm.bundles,
      [types.PHONECASE]: vm.bundles,
      [types.HAT]: vm.hatBundles,
      [types.BEANIE]: vm.beanieBundles,
      [types.TOTE]: vm.toteBundles,
      [types.STICKER]: vm.stickers?.bySize,
    }[vm.input.productType] ?? null
  );
}

const isSticker = (vm) => vm.input.productType === vm.productTypes.STICKER;
const takesHex = (vm) => vm.input.productType === vm.productTypes.PHONECASE;

// NylonColorStylePanel's PHONECASE_STICKER_OPTION_LIMIT: phone cases and stickers offer 3
// options whatever the account's colour limit.
const PHONECASE_STICKER_OPTION_LIMIT = 3;

/** How many colours (or sticker sizes) the page lets this product have, as its Add button does. */
export function optionLimit(vm) {
  const capped = [vm.productTypes.PHONECASE, vm.productTypes.STICKER].includes(vm.input.productType);

  return capped ? PHONECASE_STICKER_OPTION_LIMIT : vm.colorLimit;
}

/**
 * Each field as it stood before a batch of changes: every `expect` in a batch is the field as the
 * assistant last saw it, so a second change to the same field (two colours added at once) is
 * compared with that, not with the page after the first change.
 */
function startingState(vm) {
  return {
    title: (vm.input.meta.title ?? "").trim(),
    description: (vm.input.meta.description ?? "").trim(),
    colors: colorNames(vm),
    placement: vm.input.placement,
    campaign: { start: vm.input.campaignStart, end: vm.input.campaignEnd },
    print_method: vm.input.productionMethod,
    inks: vm.input.numInks,
    ink_type: vm.input.inkType,
    ink_effect: vm.input.inkEffect,
    publish_when_ready: vm.input.shouldPublish?.id === "yes",
    offset: { x: Number(vm.input.printOffset?.x ?? 0), y: Number(vm.input.printOffset?.y ?? 0) },
    synchronize_prices: !!vm.synchronizePrices,
    prices: Object.fromEntries(priceBoxes(vm).map(({ box, priceGroup }) => [box, money(priceGroup.displayItem.pricing?.retail)])),
  };
}

const round2 = (value) => Math.round(Number(value) * 100) / 100;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * The custom placement on the side the artwork prints now (front, back or pocket), from those
 * the page offers, or null when it offers none (some product types, the back neck's own custom).
 */
function customPlacementFor(vm) {
  const customs = (vm.placements ?? []).map((placement) => placement.name).filter(isCustomPlacement);
  const now = vm.input.placement ?? "";

  if (isCustomPlacement(now)) {
    return now;
  }

  const side = now.startsWith("back+") ? "back+" : now.startsWith("pocket") ? "pocket" : "";

  return customs.find((name) => (side ? name.startsWith(side) : !name.startsWith("back+") && !name.startsWith("pocket"))) ?? null;
}

/**
 * Switch to the custom placement with this box (inches), as picking Custom and typing its width
 * and height would: the box the artwork is fitted into. Keeps any nudge already set.
 */
function setCustomBox(vm, custom, box) {
  if (vm.input.placement !== custom) {
    vm.onPlacementChange(custom);
  }

  vm.input.printGeometry.width = vm.debouncedInput.printGeometry.width = round2(box.width);
  vm.input.printGeometry.height = vm.debouncedInput.printGeometry.height = round2(box.height);
}

/** The artwork's printed size now, "w × h in", or null. */
function printedSize(vm) {
  const box = placementBox(vm.input.placement, vm.input.printGeometry);
  const raster = vm.rasterGeometry;

  if (!box || !(raster?.width > 1) || !(raster?.height > 1)) {
    return null;
  }

  const printed = calculateArtworkGeometry(1, box, raster);

  return `${Number(printed.artworkWidthInches).toFixed(1)} × ${Number(printed.artworkHeightInches).toFixed(1)} in`;
}

/**
 * The items a colour could carry on this page: this product's sales and print method's (a bundle
 * lists each item once per method), and not ones it can't print or has no stock of.
 */
const choosableItems = (vm, color) =>
  (color.bundle?.items ?? []).filter(
    (item) =>
      !(item.sales_method && item.sales_method !== vm.input.salesMethod) &&
      !(item.production_method && item.production_method !== vm.input.productionMethod) &&
      item.canBeIncluded !== false &&
      item.isStocked !== false &&
      item.canBePrinted !== false,
  );

/** A colour's items as the page counts them (included and valid), each once. */
const includedItems = (vm, color) => [
  ...new Set(
    choosableItems(vm, color)
      .filter((item) => item.isIncludedAndValid ?? item.isIncluded)
      .map(itemName),
  ),
];

function placementNames(vm) {
  return (vm.placements ?? []).map((placement) => placement.name).filter((name) => !isCustomPlacement(name));
}

/** What the assistant may pick from, for the snapshot: colours (or sticker sizes) it can add, and placements. */
export function changeOptions(vm) {
  // A custom sticker size needs the size controls, so it stays a click.
  const addable = (bundleList(vm) ?? [])
    .filter((bundle) => bundle.name !== "Custom" && vm.canAddBundle(bundle))
    // A sticker size goes by its size ("3x3"), as the chosen ones do.
    .map((bundle) => (isSticker(vm) ? bundle.size : bundle.name))
    .slice(0, 80);

  return {
    ...(isSticker(vm) ? { addableSizes: addable } : { addableColors: bundleList(vm) ? addable : null }),
    ...(takesHex(vm) ? { anyHexColor: true } : {}),
    // How many colours (or sizes) the page allows this product.
    colorLimit: optionLimit(vm),
    placements: placementNames(vm),
  };
}

/** Apply each change in order; one that fails doesn't stop the rest. */
export function applyCreatorChanges(vm, changes) {
  const start = startingState(vm);

  return (Array.isArray(changes) ? changes : []).map((change) => {
    try {
      return { kind: change?.kind, ...applyChange(vm, change ?? {}, start) };
    } catch {
      return { kind: change?.kind, ...refused("The creator couldn’t make that change. Ask the seller to make it on the page.") };
    }
  });
}

function applyChange(vm, change, start) {
  const hasArtwork = !!vm.input.artwork?.rasterArtworkPathInfo;
  // Colours, prices and placement change on the Builder step, as on the page (its Next button
  // checks them before Details).
  const inBuilder = hasArtwork && vm.step === vm.steps.BUILDER;

  switch (change.kind) {
    case "title":
    case "description": {
      // Compared trimmed: what reaches the assistant has been trimmed on the way.
      if (start[change.kind] !== String(change.expect ?? "").trim()) {
        return stale(vm.input.meta[change.kind] ?? "");
      }

      const limit = change.kind === "title" ? 85 : 2000;
      vm.input.meta[change.kind] = String(change.value ?? "").slice(0, limit);

      return applied(vm.input.meta[change.kind]);
    }

    case "add_color":
    case "remove_color":
    case "add_size":
    case "remove_size": {
      const sizeKind = change.kind.endsWith("_size");

      if (sizeKind !== isSticker(vm)) {
        return refused(isSticker(vm) ? "Stickers are chosen by size: use add_size or remove_size." : "Only stickers are chosen by size: use add_color or remove_color.");
      }

      if (!inBuilder) {
        return refused(`${sizeKind ? "Sizes" : "Colours"} are chosen on the Builder step, once the artwork is uploaded.`);
      }

      const now = colorNames(vm);

      if (!same(start.colors, change.expect)) {
        return stale(now);
      }

      const match = sizeKind ? sizeNorm : norm;
      const wanted = match(change.value);

      if (change.kind.startsWith("remove")) {
        const index = vm.input.colors.findIndex((color) => match(colorName(color)) === wanted);

        if (index === -1) {
          return refused(`${change.value} isn’t one of the chosen ${sizeKind ? "sizes" : "colours"}.`);
        }

        // The page won't leave the Builder with nothing chosen.
        if (vm.input.colors.length === 1) {
          return refused(`That’s the only ${sizeKind ? "size" : "colour"}: add another before removing it.`);
        }

        vm.onDeleteColor(index);

        return applied(colorNames(vm));
      }

      const list = bundleList(vm);

      if (!list) {
        return refused("This product’s colour is picked on the page.");
      }

      if (vm.input.colors.length >= optionLimit(vm)) {
        return refused(`This product already has the most ${sizeKind ? "sizes" : "colours"} allowed (${optionLimit(vm)}).`);
      }

      const index = list.findIndex((bundle) => bundle.name !== "Custom" && [bundle.name, bundle.size].some((name) => name && match(name) === wanted));

      if (index !== -1) {
        if (!vm.canAddBundle(list[index])) {
          return refused(`${list[index].name} can’t be added: it’s already chosen, or nothing in it can be printed with this artwork and placement.`);
        }

        vm.onChooseColor(undefined, index);

        return applied(colorNames(vm));
      }

      // A phone case colour can be any hex colour, as the picker's custom swatch allows.
      const hex = String(change.value ?? "").trim();

      if (takesHex(vm) && isHex(hex)) {
        if (now.some((name) => String(name).toLowerCase() === hex.toLowerCase())) {
          return refused(`${hex} is already chosen.`);
        }

        vm.onChooseColor(undefined, undefined, hex);

        return applied(colorNames(vm));
      }

      return refused(
        `There’s no ${sizeKind ? "size" : "colour"} called ${change.value}. Use one from ${sizeKind ? "addableSizes" : "addableColors"}${takesHex(vm) ? ", or send a close hex colour like #1A3D6E instead" : ""}.`,
      );
    }

    case "set_price": {
      // The price boxes are on the Details step (the pricing table); the Builder is where items are chosen.
      if (!hasArtwork || ![vm.steps.BUILDER, vm.steps.DETAILS].includes(vm.step)) {
        return refused("Prices are set on the Details step, once something is chosen.");
      }

      const found = priceBoxes(vm).find(({ box }) => box === change.box);

      if (!found) {
        return refused(`There’s no price box called ${change.box}. Use a box from prices.`);
      }

      const priceGroup = found.priceGroup;
      const now = money(priceGroup.displayItem.pricing?.retail);
      // Synchronized prices move every box, so a second price in a batch compares with the box
      // as it stood before the batch (a box added in the batch compares with its price now).
      const before = change.box in start.prices ? start.prices[change.box] : now;

      if (before !== money(change.expect)) {
        return stale(now);
      }

      // Exactly as typing it into that box: whole dollars within its bounds, and with
      // synchronized prices on, every price moves by the same amount.
      if (vm.setGroupPrice(priceGroup, change.value) === null) {
        return refused(`${change.value} isn’t a price.`);
      }

      return applied({
        retail: money(priceGroup.displayItem.pricing?.retail),
        earnsPerSale: money(priceGroup.displayItem.pricing?.cutPlusBonus),
        otherPricesMoved: !!vm.synchronizePrices,
      });
    }

    case "replace_artwork": {
      // An artwork edit by the assistant (edit_artwork): a new staged file, used exactly as an
      // upload is, if the page still shows the artwork the edit was made from.
      const second = change.location === "second";

      if (second && !vm.input.secondLocation) {
        return refused("There’s no second print location any more.");
      }

      const pathInfo = second ? vm.input.secondLocation.artwork?.rasterArtworkPathInfo : vm.input.artwork?.rasterArtworkPathInfo;
      const now = pathInfo?.basename ?? null;

      if (now !== change.expect) {
        return stale(now);
      }

      const staged = change.value;

      if (!staged?.pathInfo?.basename || !staged?.untrimmed?.pathInfo?.basename) {
        return refused("The edited artwork didn’t arrive.");
      }

      if (second) {
        vm.useStagedSecondArtwork(staged);
      } else {
        vm.useStagedArtwork(staged);
      }

      return applied((second ? vm.input.secondLocation.artwork : vm.input.artwork).rasterArtworkPathInfo.basename);
    }

    case "campaign_dates": {
      if (vm.input.salesMethod !== vm.salesMethods.CAMPAIGN) {
        return refused("Only a campaign has dates.");
      }

      if (![vm.steps.BUILDER, vm.steps.DETAILS].includes(vm.step) || !hasArtwork) {
        return refused("Campaign dates are set on the Details step.");
      }

      const now = { start: vm.input.campaignStart, end: vm.input.campaignEnd };

      if (!same(start.campaign, { start: change.expect?.start ?? null, end: change.expect?.end ?? null })) {
        return stale(now);
      }

      // As the page's inputs hold them: local time to the minute.
      const local = (value) => {
        const date = DateTime.fromISO(String(value ?? ""));

        return date.isValid ? date.set({ second: 0, millisecond: 0 }).toISO({ format: "extended", suppressSeconds: true, suppressMilliseconds: true, includeOffset: false }) : null;
      };

      // A live campaign keeps its start; only its end moves (the page anchors the window to it).
      const live = vm.campaignStartMin === null;
      const startValue = live || change.value?.start === undefined ? vm.input.campaignStart : local(change.value.start);
      const endValue = change.value?.end === undefined ? vm.input.campaignEnd : local(change.value.end);

      if (!startValue || !endValue) {
        return refused("Dates need to be like 2026-11-02T14:00 (local time).");
      }

      // The page's own bounds: no start in the past, the end 24 hours to 30 days after the start.
      if (!live && DateTime.fromISO(startValue) < DateTime.fromISO(vm.campaignStartMin)) {
        return refused("The start can’t be in the past.");
      }

      const startDate = DateTime.fromISO(live ? vm.campaignStartForWindow : startValue);

      if (DateTime.fromISO(endValue) < startDate.plus({ hours: 24 })) {
        return refused("The end must be at least 24 hours after the start.");
      }

      if (DateTime.fromISO(endValue) > startDate.plus({ days: 30 })) {
        return refused("The end can be at most 30 days after the start.");
      }

      // Through the inputs' validation models, as typing a date does.
      const model = vm.v$?.input;

      if (model?.campaignStart && !live) {
        model.campaignStart.$model = startValue;
      } else if (!live) {
        vm.input.campaignStart = startValue;
      }

      if (model?.campaignEnd) {
        model.campaignEnd.$model = endValue;
      } else {
        vm.input.campaignEnd = endValue;
      }

      return applied({ start: vm.input.campaignStart, end: vm.input.campaignEnd });
    }

    case "print_method": {
      const methods = printMethods(vm);

      if (!methods) {
        return refused("Only shirts offer a choice of print method.");
      }

      if (!inBuilder) {
        return refused("The print method is chosen on the Builder step.");
      }

      if (start.print_method !== change.expect) {
        return stale(vm.input.productionMethod);
      }

      if (!methods.includes(change.value)) {
        return refused(`${change.value} isn’t offered here. Use one of printMethods.`);
      }

      // The page's own switch: it resets the placement and print size, and drops a back print.
      vm.onProductionMethodChange(change.value);

      return applied({ printMethod: vm.input.productionMethod, placement: vm.input.placement, secondLocation: !!vm.input.secondLocation });
    }

    case "inks":
    case "ink_type":
    case "ink_effect": {
      if (vm.input.productionMethod !== vm.productionMethods.SCREENPRINT) {
        return refused("Inks are only chosen for screen printing.");
      }

      if (!inBuilder) {
        return refused("Inks are chosen on the Builder step.");
      }

      const field = { inks: "numInks", ink_type: "inkType", ink_effect: "inkEffect" }[change.kind];

      if (start[change.kind] !== change.expect) {
        return stale(vm.input[field]);
      }

      if (change.kind === "inks") {
        const count = Number(change.value);

        if (!Number.isInteger(count) || count < 1 || count > 8) {
          return refused("Screen printing takes 1 to 8 inks.");
        }

        vm.onClickNumInks(count);
      } else {
        const options = change.kind === "ink_type" ? vm.inkTypes : vm.inkEffects;

        if (!Object.hasOwn(options ?? {}, change.value)) {
          return refused(`${change.value} isn’t an option. Use one of inkOptions.`);
        }

        if (change.kind === "ink_type") {
          vm.onInkTypeChange(change.value);
        } else {
          vm.onInkEffectChange(change.value);
        }
      }

      return applied(vm.input[field]);
    }

    case "publish_when_ready": {
      // The page only offers it when it can matter (a product that launches on submit, or
      // embroidery on demand); campaigns publish at launch.
      if (!(vm.willLaunchAfterSubmission || vm.isEodSubmission)) {
        return refused("This product doesn’t have that choice: campaigns publish when they launch, and a product that goes to review is published when it’s approved.");
      }

      if (start.publish_when_ready !== change.expect) {
        return stale(vm.input.shouldPublish?.id === "yes");
      }

      vm.onPublishSelected({ id: change.value ? "yes" : "no" });

      return applied(vm.input.shouldPublish?.id === "yes");
    }

    case "placement": {
      if (!inBuilder) {
        return refused("The placement is chosen on the Builder step.");
      }

      const now = vm.input.placement;

      if (start.placement !== change.expect) {
        return stale(now);
      }

      if (!placementNames(vm).includes(change.value)) {
        return refused(`${change.value} isn’t a placement this product offers. Use one from placements.`);
      }

      vm.onPlacementChange(change.value);

      return applied(vm.input.placement);
    }

    case "print_width": {
      // An exact printed width: the custom placement, with its box sized so the artwork prints
      // that wide (as the seller would type it into Custom's width and height).
      if (!inBuilder) {
        return refused("The print size is set on the Builder step, once artwork is uploaded.");
      }

      if (start.placement !== change.expect) {
        return stale(vm.input.placement);
      }

      const custom = customPlacementFor(vm);
      const raster = vm.rasterGeometry;
      const width = Number(change.value);

      if (!custom) {
        return refused("This product's placements don't take a custom size.");
      }

      if (!(width > 0) || !(raster?.width > 1) || !(raster?.height > 1)) {
        return refused(`${change.value} isn’t a width in inches.`);
      }

      const size = getCustomSize(vm.input.productType, custom);
      const ratio = Number(raster.height) / Number(raster.width);
      let boxWidth = clamp(width, size.min.width, size.max.width);
      let boxHeight = boxWidth * ratio;

      // Too tall for the print area at that width: as wide as the tallest box allows.
      if (boxHeight > size.max.height) {
        boxHeight = size.max.height;
        boxWidth = boxHeight / ratio;
      }

      setCustomBox(vm, custom, { width: boxWidth, height: Math.max(boxHeight, size.min.height) });

      return applied({
        placement: vm.input.placement,
        printedInches: printedSize(vm),
        limitedTo: round2(boxWidth) < round2(width) ? `the most this print area allows (${size.max.width} × ${size.max.height} in)` : null,
      });
    }

    case "nudge": {
      // Move the artwork from where it sits (inches; y above 0 is up), as the Offset X / Y inputs
      // do. Only a custom placement nudges, so a named one becomes custom at the same size first.
      if (!inBuilder) {
        return refused("The print position is set on the Builder step, once artwork is uploaded.");
      }

      if (!same(start.offset, change.expect)) {
        return stale({ x: Number(vm.input.printOffset?.x ?? 0), y: Number(vm.input.printOffset?.y ?? 0) });
      }

      const custom = customPlacementFor(vm);

      if (!custom) {
        return refused("This product's placements can't be nudged.");
      }

      if (vm.input.placement !== custom) {
        setCustomBox(vm, custom, placementToBoxMap[vm.input.placement] ?? getCustomSize(vm.input.productType, custom).default);
      }

      const range = vm.offsetRange;
      const wanted = { x: Number(change.value?.x ?? vm.input.printOffset.x ?? 0), y: Number(change.value?.y ?? vm.input.printOffset.y ?? 0) };

      if (!Number.isFinite(wanted.x) || !Number.isFinite(wanted.y)) {
        return refused("A nudge is {x, y} in inches.");
      }

      const x = round2(clamp(wanted.x, range.min.x, range.max.x));
      const y = vm.primaryIsBackNeck ? 0 : round2(clamp(wanted.y, range.min.y, range.max.y));
      vm.input.printOffset.x = vm.debouncedInput.printOffset.x = x;
      vm.input.printOffset.y = vm.debouncedInput.printOffset.y = y;

      return applied({ placement: vm.input.placement, offset: { x, y }, range, limited: x !== round2(wanted.x) || y !== round2(wanted.y) });
    }

    case "items": {
      // Which items each chosen colour is offered on, as the Builder's item checkboxes: value
      // {keep: [names]} keeps only items whose name contains one of these ("Tri-Blend", "6010"),
      // {remove: [names]} unticks those; every chosen colour, as far as it has them.
      if (!inBuilder) {
        return refused("Items are chosen on the Builder step, once artwork is uploaded.");
      }

      const keep = (change.value?.keep ?? []).map(norm).filter(Boolean);
      const remove = (change.value?.remove ?? []).map(norm).filter(Boolean);
      const matches = (item, names) => names.some((name) => norm(itemName(item)).includes(name));

      if (!keep.length && !remove.length) {
        return refused("Say which items to keep ({keep: [...]}) or remove ({remove: [...]}).");
      }

      const colors = vm.input.colors.filter((color) => color.bundle);

      if (keep.length && !colors.some((color) => choosableItems(vm, color).some((item) => matches(item, keep)))) {
        return refused(`None of the chosen colours comes on ${change.value.keep.join(" or ")}. Use names from the colours' items or offeredItems.`);
      }

      colors.forEach((color) =>
        choosableItems(vm, color).forEach((item) => {
          if (keep.length) {
            item.isIncluded = matches(item, keep);
          } else if (matches(item, remove)) {
            item.isIncluded = false;
          }
        }),
      );

      const now = colors.map((color) => ({ color: colorName(color), items: includedItems(vm, color) }));

      return applied({ colors: now, colorsLeftWithNoItems: now.filter((color) => !color.items.length).map((color) => color.color) });
    }

    case "synchronize_prices": {
      // The pricing table's switch: on, changing one price moves every price by the same amount;
      // off, each box is its own.
      if (start.synchronize_prices !== !!change.expect) {
        return stale(!!vm.synchronizePrices);
      }

      if (!!vm.synchronizePrices !== !!change.value) {
        vm.onSynchronizePricesChange();
      }

      return applied(!!vm.synchronizePrices);
    }

    default:
      return refused(`The creator doesn’t know how to change ${change.kind}.`);
  }
}
